/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkStyleProps, ILinkStyles, Link } from '@fluentui/react/lib/Link';
import { IListProps, List } from '@fluentui/react/lib/List';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';

export type EntityMigrationNotificationCardProps = {
    /**
     * The list of apps that need to be upgraded.
     */
    appsToUpgrade: { id: string; name: string; activeVersion: string }[];
};

const messages = defineMessages({
    detailedMessage: {
        id: 'portal.entityMigrationToastContent.detailedMessage',
        defaultMessage: 'We have detected composite entities active in {appsToUpgradeCount} of your apps.'
    },
    goToAppTitle: {
        id: 'portal.entityMigrationToastContent.goToAppTitle',
        defaultMessage: 'Go to each app to upgrade:'
    },
    learnMore: {
        id: 'portal.entityMigrationToastContent.learnMore',
        defaultMessage: 'Learn more about upgrading composite entities'
    }
});

const { styleComponent } = getStylistV2('EntityMigrationNotificationCard');
const ScrollableList = styleComponent<IListProps>(List)('ScrollableList', { overflow: 'auto', maxHeight: '60px' });
const linkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({ root: { display: 'flex' } });

/**
 * Displays the content of the notification card to notify
 * users to upgrade their apps that contain composite entities
 * with showing a list of those apps.
 */
export const EntityMigrationNotificationCard = (props: EntityMigrationNotificationCardProps) => {
    const { appsToUpgrade } = props;
    const history = useHistory();
    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();

    /**
     * Navigates the user to the clicked app.
     */
    const goToApp = (appId: string, activeVersion: string) => {
        dispatcher.dispatch('updateActiveGlobalBlade', { newBlade: null });

        dispatcher.dispatch('openConversationApp', { context: { appId } });

        history.push(routeTable.luis.applicationPage(appId, activeVersion));
    };

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <LuisText data-automation-id="detailedMessage">
                {localize(messages.detailedMessage, { appsToUpgradeCount: appsToUpgrade.length })}
            </LuisText>

            <LuisText>{localize(messages.goToAppTitle)}</LuisText>

            <ScrollableList
                items={appsToUpgrade}
                onRenderCell={(app: { id: string; name: string; activeVersion: string }) => (
                    <Link styles={linkStyles} onClick={() => goToApp(app.id, app.activeVersion)}>
                        <TruncatedText>{app.name}</TruncatedText>
                    </Link>
                )}
            />

            <ExternalLink styles={linkStyles} href="https://go.microsoft.com/fwlink/?linkid=2128287">
                {localize(messages.learnMore)}
            </ExternalLink>
        </Stack>
    );
};
